// Runtime side of the brand identity contract: load /profile-config.json,
// validate its optional `branding` block (src/lib/branding-schema.ts), and
// apply the document-level pieces (title, colour custom properties, favicon,
// theme-color) before React renders. Components read the resolved identity
// through the BrandingContext below.

import { createContext, useContext } from "react"
import {
  Branding,
  BrandingConfigError,
  COLOR_KEY_TO_CSS_VAR,
  DEFAULT_BRANDING,
  parseBranding,
  type BrandingColors,
} from "./branding-schema"

export { BrandingConfigError, DEFAULT_BRANDING, parseBranding, type Branding, type BrandingColors }

/**
 * Resolve the deployment's branding from /profile-config.json.
 *
 * Failure semantics mirror the schema contract:
 *   - fetch failed or non-2xx (no profile served, e.g. bare `vite preview`
 *     of a foreign build): the shipped Devoteam defaults, with a console
 *     warning. Absence is a supported state.
 *   - response exists but is not JSON, or its `branding` block is invalid:
 *     throws BrandingConfigError. A deployment that ships a broken profile
 *     must fail visibly, not render half a brand.
 */
export async function loadBranding(fetchImpl: typeof fetch = fetch): Promise<Branding> {
  let response: Response
  try {
    response = await fetchImpl("/profile-config.json")
  } catch (error) {
    console.warn("Profile config unreachable, using shipped branding defaults:", error)
    return DEFAULT_BRANDING
  }
  if (!response.ok) {
    console.warn(
      `Profile config request returned ${response.status}, using shipped branding defaults.`
    )
    return DEFAULT_BRANDING
  }
  let profile: unknown
  try {
    profile = await response.json()
  } catch {
    throw new BrandingConfigError("/profile-config.json is not valid JSON")
  }
  if (typeof profile !== "object" || profile === null || Array.isArray(profile)) {
    throw new BrandingConfigError("/profile-config.json must contain a JSON object")
  }
  return parseBranding((profile as Record<string, unknown>).branding)
}

/**
 * Semantic tokens in globals.css whose shipped literals are the brand accent
 * family (the shadcn layer: buttons, focus rings, the first chart series).
 * The stylesheet cannot route them through var(--brand-coral) the way the
 * brand utilities are routed: the shipped literals are oklch and the brand
 * tokens hsl, so indirection would nudge the default computed values. They
 * are rewritten at runtime instead, and only when the pack overrides the
 * corresponding colour, which keeps an unconfigured deployment
 * byte-identical to the shipped Devoteam UI.
 */
const ACCENT_SEMANTIC_VARS = ["--primary", "--sidebar-primary", "--chart-1"] as const
/** Focus rings ship at 40% alpha of the accent; color-mix reproduces that. */
const ACCENT_RING_VARS = ["--ring", "--sidebar-ring"] as const

/**
 * Point every accent-valued token (brand utilities, buttons, focus rings,
 * chart series 1) at one colour. Shared by applyBranding (a pack's
 * `colors.primary`) and the embed panel's host accent hint, so neither path
 * can produce a half-rethemed UI where brand utilities change colour but the
 * primary buttons keep the previous accent.
 *
 * @param color A validated plain CSS colour (see isValidBrandingColor); the
 *   caller is responsible for validation because the value lands in inline
 *   styles on the document element.
 * @param doc Document to retheme; defaults to the global document.
 */
export function applyAccentColor(color: string, doc: Document = document): void {
  const root = doc.documentElement
  root.style.setProperty("--brand-coral", color)
  for (const cssVar of ACCENT_SEMANTIC_VARS) root.style.setProperty(cssVar, color)
  const ring = `color-mix(in srgb, ${color} 40%, transparent)`
  for (const cssVar of ACCENT_RING_VARS) root.style.setProperty(cssVar, ring)
}

/**
 * Apply the document-level branding: tab title, colour custom properties,
 * favicon and theme-color. Called once per entry point, before render.
 *
 * Only colour keys the pack actually provided are written, so a deployment
 * without a `colors` block leaves the stylesheet completely untouched and
 * renders byte-identically to the shipped Devoteam UI.
 */
export function applyBranding(
  branding: Branding,
  mode: "standalone" | "panel",
  doc: Document = document
): void {
  doc.title = mode === "panel" ? branding.panelTitle : branding.browserTitle

  const root = doc.documentElement
  for (const [key, cssVar] of Object.entries(COLOR_KEY_TO_CSS_VAR)) {
    const value = branding.colors[key as keyof BrandingColors]
    if (value) root.style.setProperty(cssVar, value)
  }

  // The semantic layer: primary drives the button/ring/chart accents, and the
  // wash/deep shades drive the hover-accent pair, so a re-coloured pack
  // rethemes the shadcn surfaces too, not just the brand-* utilities.
  if (branding.colors.primary) applyAccentColor(branding.colors.primary, doc)
  if (branding.colors.primary_wash) root.style.setProperty("--accent", branding.colors.primary_wash)
  if (branding.colors.primary_deep) {
    root.style.setProperty("--accent-foreground", branding.colors.primary_deep)
  }

  if (branding.colors.primary) {
    const themeColor = doc.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    if (themeColor) themeColor.content = branding.colors.primary
  }

  if (branding.faviconPath) {
    // rel~= (token match), not rel= (exact match): index.html carries both
    // rel="icon" and rel="alternate icon", and the exact-match selector would
    // leave the alternate .ico pointing at the previous brand's favicon.
    for (const link of doc.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')) {
      link.href = branding.faviconPath
    }
  }
}

/**
 * Resolved identity for the running deployment. Defaults to the shipped
 * Devoteam values so components render correctly under test without a
 * provider; the entry points always provide the loaded value.
 */
export const BrandingContext = createContext<Branding>(DEFAULT_BRANDING)

export function useBranding(): Branding {
  return useContext(BrandingContext)
}
